<script>
import productCardMixin from '@/v-shop/mixins/product-card-mixin'

export default {
	mixins: [productCardMixin],
}
</script>

<template>
	<v-container :class="$bem('product-data-sheet', 'secondary no-mobile-px py-0')">
		<!-- DESKTOP VERSION -->
		<v-row :class="$bem('__product-view')" v-if="$b.d" class="my-0">
			<v-col cols="4">
				<v-hover v-slot="{ hover }">
					<div :class="$bem('__image-cont')" @mouseover="firstImageHover = true">
						<Media cover :class="$bem('__main-image')" :src="mainImage" width="100%" aspect-ratio="1" />
						<div v-if="secondImage && firstImageHover" :class="$bem('__second-image-cont')">
							<Media
								cover
								:src="secondImage"
								width="100%"
								:class="[$bem('__second-image'), hover ? $bem('__second-image--show') : '']"
							/>
						</div>
					</div>
				</v-hover>
			</v-col>
			<v-col cols="8" class="py-15 d-flex flex-column justify-space-around" style="position: relative">
				<h3 class="font-3 header">{{ product.name }}</h3>
				<div class="d-flex align-center">
					<div>
						<div class="d-flex align-baseline mt-2 font-2" v-if="discountPct">
							<del class="grey--text">
								<PriceText
									:amount="prevPrice"
									:currency="currency"
									:currencyMargin="0"
									:class="$bem('__prev-price')"
									priceClass="grey--text mr-2"
								/>
							</del>

							<span :class="$bem('__discount')" class="discount font-weight-medium mx-1">
								{{ discountPct }}% OFF
							</span>
						</div>
						<PriceText
							:class="$bem('__price')"
							class="price font-7 mb-5"
							:amount="price"
							:currency="currency"
						/>
					</div>

					<CollectionEventIcon
						multiple
						:product="product"
						:class="[$bem('__event-icon'), { 'mb-5': !discountPct }]"
						class="ml-4"
					/>
				</div>

				<div v-if="product.info.description">{{ product.info.description }}</div>

				<div v-if="hasTextTag">
					<div
						:class="[
							$bem('__text-tag', 'line-clamp-2 font-weight-medium primary px-2 py-1 rounded mt-2'),
							{ 'mb-2': financingTagHtml },
						]"
					>
						{{ textTag }}
					</div>
				</div>

				<div v-if="financingTagHtml && financingTagHtml.length">
					<div
						v-for="(html, n) in financingTagHtml"
						:key="n"
						:class="[
							$bem('__financing', 'line-clamp-2 font-weight-medium primary px-2 py-1 rounded'),
							{ 'my-2': !hasTextTag },
						]"
						v-html="html"
					></div>
				</div>

				<ProductCard-Tags tag-class="px-2 py-1" />

				<ProductActionsSmall :product="product" :variant="selectedVariant" />
			</v-col>
		</v-row>
		<!-- END OF DESKTOP VERSION -->

		<!-- MOBILE VERSION -->
		<div :class="$bem('__product-view', 'd-flex flex-column align-center')" v-else>
			<div :class="$bem('__imgs-container', 'mb-2')">
				<v-hover v-slot="{ hover }">
					<div :class="$bem('__image-cont')" @mouseover="firstImageHover = true">
						<Media
							cover
							:class="$bem('__main-image')"
							:src="mainImage"
							width="100%"
							aspect-ratio="1"
							:imgClass="{ smallSize: $b.mt }"
						/>
						<div v-if="secondImage && firstImageHover" :class="$bem('__second-image-cont')">
							<Media
								cover
								:src="secondImage"
								width="100%"
								:class="[$bem('__second-image'), hover ? $bem('__second-image--show') : '']"
								:imgClass="{ smallSize: $b.mt }"
							/>
						</div>
					</div>
				</v-hover>
			</div>

			<div class="d-flex flex-column justify-space-around w100">
				<h3 class="font-2 header line-clamp-1">{{ product.name }}</h3>
				<div class="d-flex align-center">
					<div>
						<div class="d-flex align-baseline mt-2 font-1" v-if="discountPct">
							<del class="grey--text">
								<PriceText
									:amount="prevPrice"
									:currency="currency"
									:currencyMargin="0"
									:class="$bem('__prev-price')"
									priceClass="grey--text mr-2"
								/>
							</del>

							<span :class="$bem('__discount')" class="discount font-weight-medium mx-2">
								{{ discountPct }}% OFF
							</span>
						</div>
						<PriceText
							:class="[$bem('__price'), discountPct ? 'mb-3' : 'mt-3']"
							class="price font-6"
							:amount="price"
							:currency="currency"
						/>
					</div>

					<CollectionEventIcon
						multiple
						:limitAmount="3"
						:product="product"
						:class="[$bem('__event-icon'), { 'mt-3': !discountPct }]"
						class="ml-4"
					/>
				</div>

				<div v-if="hasTextTag">
					<div
						:class="[
							$bem(
								'__text-tag-mobile',
								'font-weight-medium primary px-2 py-1 rounded line-clamp-1 mt-1'
							),
							{ 'mb-1': financingTagHtml },
						]"
					>
						{{ textTag }}
					</div>
				</div>

				<div v-if="financingTagHtml && financingTagHtml.length">
					<div
						v-for="(html, n) in financingTagHtml"
						:key="n"
						:class="
							$bem(
								'__financing-mobile',
								'font-weight-medium primary px-2 py-1 rounded line-clamp-1 mb-1'
							)
						"
						v-html="html"
					></div>
				</div>

				<ProductCard-Tags />

				<ProductActionsSmall :product="product" :variant="selectedVariant" class="mt-3" />
			</div>
		</div>
		<!-- END OF MOBILE VERSION -->
	</v-container>
</template>

<style scoped lang="scss">
::v-deep .smallSize {
	width: 200px !important;
	height: 200px !important;
}

::v-deep .qty-btn {
	justify-content: center;
}

::v-deep .stock-message {
	text-align: center;
}

.product-data-sheet {
	position: relative;

	&__imgs-container {
		width: 200px;
	}

	&__image-cont {
		position: relative;
	}

	&__second-image-cont {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	&__second-image {
		transition: opacity 0.3s;
		opacity: 0;
		&--show {
			opacity: 1;
		}
	}

	&__discount {
		padding: 2px 5px;
		border-radius: 4px;
	}
}
</style>

